import { create } from "zustand";

/**
 * WHY THIS IS STILL A STORE AND NOT FOUR `useState` CALLS (decided 2026-08-10).
 *
 * All four fields below are the shapes `.claude/rules/state-management.md` lists
 * under "keep `useState`" — a filter, a page size, an overlay index, a dialog
 * flag — and there is exactly ONE app importer (`GalleryClient`), which is the
 * same measurement that got `stores/sidebar-store.ts` deleted. So lifting them
 * was seriously considered. It was rejected because, unlike the sidebar, there is
 * no correctness argument for the move: the sidebar's bug (a server-derived value
 * that had to be right on the FIRST render) is one a module singleton
 * structurally cannot serve, whereas the bug here — state outliving the page —
 * is fully closed by an explicit `reset()`, which is the shape already shipped
 * for the identical defect in `stores/product-store.ts` (`resetOverlay`).
 *
 * Against that, the store is also the seam three component specs use to seed a
 * mount (`GalleryClient.spec.tsx`, `GalleryClientSsrSwap.spec.tsx`,
 * `gallery-lcp-priority.spec.tsx` all call `useGallery.setState`), and
 * `GalleryClient` reads the fields through nine atomic selectors on a render path
 * that handles ~1,084 items. Converting would rewrite that hot path and three
 * specs to fix nothing that `reset()` does not. If a SECOND consumer never
 * appears and someone later wants to lift it, that is a refactor — not this bug
 * fix.
 */
interface GalleryState {
  filter: string;
  displayCount: number;
  lightboxIndex: number | null;
  infoOpen: boolean;

  setFilter: (filter: string) => void;
  setDisplayCount: (count: number | ((prev: number) => number)) => void;
  setLightboxIndex: (index: number | null | ((prev: number | null) => number | null)) => void;
  setInfoOpen: (open: boolean) => void;
  resetForFilter: (pageSize: number) => void;
  /**
   * Clear every view field back to its default, WITHOUT touching the URL or the
   * page size hook. A zustand store is a module singleton, so it outlives the
   * page that uses it: a visitor who left /gallery with the lightbox open came
   * back to `lightboxIndex` still set and `:702` rendering the lightbox
   * immediately, on a photo they never tapped this visit (`infoOpen: true`
   * re-opened the Sheet/Dialog the same way). Worse than the /products
   * precedent, because the index is resolved against a FRESHLY computed
   * `filtered` array — so a stale index can land on a DIFFERENT photo.
   * `GalleryClient` calls this on unmount so the next mount starts clean.
   *
   * Deliberately NOT `resetForFilter`: that one keeps `filter` and `infoOpen`,
   * which are exactly two of the four fields that leak. And deliberately not a
   * `setLightboxIndex(null)` + `setInfoOpen(false)` pair at the call site — two
   * setters at an unmount is how one of them gets forgotten when a fifth field
   * is added here.
   */
  reset: () => void;
}

/**
 * The store's defaults, as a FACTORY rather than a shared object literal: a
 * store is a module singleton and a Workers isolate is reused across requests,
 * so a shared mutable default can leak between them. `create()` and `reset()`
 * both read it, which is what stops the two copies from drifting apart — and is
 * why a spec should reset through `reset()` rather than restating the fields.
 */
const initialGalleryState = (): Pick<
  GalleryState,
  "filter" | "displayCount" | "lightboxIndex" | "infoOpen"
> => ({
  filter: "All",
  displayCount: 6,
  lightboxIndex: null,
  infoOpen: false,
});

export const useGallery = create<GalleryState>((set) => ({
  ...initialGalleryState(),

  setFilter: (filter) => set({ filter }),

  setDisplayCount: (count) =>
    set((state) => ({
      displayCount: typeof count === "function" ? count(state.displayCount) : count,
    })),

  setLightboxIndex: (index) =>
    set((state) => ({
      lightboxIndex: typeof index === "function" ? index(state.lightboxIndex) : index,
    })),

  setInfoOpen: (open) => set({ infoOpen: open }),

  /** Reset display count and lightbox index when filter changes */
  resetForFilter: (pageSize) =>
    set({ displayCount: pageSize, lightboxIndex: null }),

  reset: () => set(initialGalleryState()),
}));
